import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import { getActivePackagesByRoom, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { EmptyObject } from 'lib/object/objectUtils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

interface Props {
  rooms?: Array<App.Occupants>;
  cheapestSelectedOption: OfferPackageOptionView;
  multipleRoomPackages: PackageOptionViewByRoom;
  canRedeemLuxPlusBenefits: boolean;
}

function HotelFlightsToggleButton(props: Props) {
  const { rooms, cheapestSelectedOption, multipleRoomPackages, canRedeemLuxPlusBenefits } = props

  const {
    flightsAvailable,
    pricing: hotelPrice,
    memberPricing: hotelMemberPrice,
    pricingWithFlights,
    memberPricingWithFlights,
  } = useMemo(() => cheapestSelectedOption, [cheapestSelectedOption])

  const { multipleRoomHotelPrice, multipleRoomHotelMemberPrice, multipleRoomFlightPrice, multipleRoomMemberFlightPrice } = useMemo(() => (
    Object.values(multipleRoomPackages || {}).reduce((acc, pkg) => {
      if (!pkg) return acc

      const {
        hotelPrice,
        hotelMemberPrice,
        flightPrice,
        propertyFees = 0,
      } = pkg

      return {
        multipleRoomHotelPrice: acc.multipleRoomHotelPrice + hotelPrice + propertyFees,
        multipleRoomHotelMemberPrice: acc.multipleRoomHotelMemberPrice + hotelMemberPrice + propertyFees,
        multipleRoomFlightPrice: acc.multipleRoomFlightPrice + hotelPrice + propertyFees + flightPrice,
        multipleRoomMemberFlightPrice: acc.multipleRoomMemberFlightPrice + hotelMemberPrice + propertyFees + flightPrice,
      }
    }, { multipleRoomHotelPrice: 0, multipleRoomHotelMemberPrice: 0, multipleRoomFlightPrice: 0, multipleRoomMemberFlightPrice: 0 })
  ), [multipleRoomPackages])

  const hotelTotalPrice = useMemo(() => {
    if (canRedeemLuxPlusBenefits && hotelMemberPrice?.price) return (rooms?.length ?? 0) > 1 ? multipleRoomHotelMemberPrice : hotelMemberPrice.price
    return (rooms?.length ?? 0) > 1 ? multipleRoomHotelPrice : hotelPrice.price
  }, [canRedeemLuxPlusBenefits, hotelMemberPrice, rooms, multipleRoomHotelPrice, multipleRoomHotelMemberPrice, hotelPrice])

  const hotelFlightTotalPrice = useMemo(() => {
    if (canRedeemLuxPlusBenefits && memberPricingWithFlights?.price) return (rooms?.length ?? 0) > 1 ? multipleRoomMemberFlightPrice : memberPricingWithFlights.price
    return (rooms?.length ?? 0) > 1 ? multipleRoomFlightPrice : pricingWithFlights?.price ?? 0
  }, [canRedeemLuxPlusBenefits, memberPricingWithFlights, rooms?.length, multipleRoomMemberFlightPrice, multipleRoomFlightPrice, pricingWithFlights])

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={flightsAvailable}
    isMemberPrice={!!hotelMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelFlightTotalPrice}
  />
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    rooms: state.rooms,
    cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
    multipleRoomPackages: isMultiRoom ? getActivePackagesByRoom(state, appState) : EmptyObject,
    canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
    open: !!state.activeFilter,
  }
}, HotelFlightsToggleButton, preventRenderWithOpenFilter)
